/**
 * CostSummary Component - Displays cost parameters and calculations
 * Requirements: 2.3, 6.3
 */

import { CostParameters } from '../services/api';

interface CostSummaryProps {
  costParameters: CostParameters;
  totalCases: number;
}

function CostSummary({ costParameters, totalCases }: CostSummaryProps) {
  const formatCurrency = (amount: number): string => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}MM`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount.toFixed(2)}`;
  };

  const formatCurrencyFull = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const variableCost = costParameters.totalBudget - costParameters.fixedCost;
  const costPerCase = totalCases > 0 ? costParameters.fixedCost / totalCases : 0;

  return (
    <div style={styles.container}>
      <div style={styles.grid}>
        <div style={styles.card}>
          <span style={styles.cardLabel}>Total Budget</span>
          <span style={styles.cardValue}>{formatCurrency(costParameters.totalBudget)}</span>
          <span style={styles.cardSubtext}>{formatCurrencyFull(costParameters.totalBudget)}</span>
        </div>

        <div style={styles.card}>
          <span style={styles.cardLabel}>Fixed Cost</span>
          <span style={styles.cardValue}>{formatCurrency(costParameters.fixedCost)}</span>
          <span style={styles.cardSubtext}>
            {(costParameters.fixedCostPercentage * 100).toFixed(0)}% of budget
          </span>
        </div>

        <div style={styles.card}>
          <span style={styles.cardLabel}>Variable Cost</span>
          <span style={styles.cardValue}>{formatCurrency(variableCost)}</span>
          <span style={styles.cardSubtext}>
            {((1 - costParameters.fixedCostPercentage) * 100).toFixed(0)}% of budget
          </span>
        </div>

        <div style={styles.card}>
          <span style={styles.cardLabel}>Cost Per Case</span>
          <span style={styles.cardValue}>${costPerCase.toFixed(2)}</span>
          <span style={styles.cardSubtext}>Fixed cost / total cases</span>
        </div>
      </div>

      <div style={styles.details}>
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>Incremental Case Impact</span>
          <span style={styles.detailValue}>
            ${costParameters.incrementalCaseImpact.toFixed(2)} decrease per 1MM cases
          </span>
        </div>
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>Extended Value per 1MM Cases</span>
          <span style={styles.detailValue}>
            {formatCurrency(costParameters.extendedValuePerMillion)}
          </span>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '0.5rem 0',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '1rem',
    marginBottom: '1rem',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0.75rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    textAlign: 'center',
  },
  cardLabel: {
    fontSize: '0.75rem',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '0.25rem',
  },
  cardValue: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#333',
  },
  cardSubtext: {
    fontSize: '0.75rem',
    color: '#888',
    marginTop: '0.25rem',
  },
  details: {
    borderTop: '1px solid #eee',
    paddingTop: '1rem',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    borderBottom: '1px solid #f0f0f0',
  },
  detailLabel: {
    fontSize: '0.85rem',
    color: '#666',
  },
  detailValue: {
    fontSize: '0.85rem',
    color: '#333',
    fontWeight: 500,
  },
};

export default CostSummary;
